import { Box, Button, Checkbox, FormControlLabel, Link, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import googleLogo from '../../../../assets/img/google/google.svg';
import classLinkLogo from '../../../../assets/img/classlink/Classlink.svg';
import cleverLogo from '../../../../assets/img/clever/Clever.svg';
import { signupAccountMethodsStyles } from './SignupAccountMethods.styles';

export type SignupProvider = 'google' | 'canvas' | 'classlink' | 'clever' | 'schoology';

interface SignupAccountMethodsProps {
  isProductView: boolean;
  requiredProvider?: SignupProvider;
  canAttemptSignup: boolean;
  isWorking: boolean;
  emailAddress: string;
  hasAcceptedTerms: boolean;
  showConsentError: boolean;
  showEmailError?: boolean;
  actionError: string;
  onEmailChange: (email: string) => void;
  onTermsChange: (accepted: boolean) => void;
  onProviderSignUp: (provider: SignupProvider) => void;
}

const providerOptions: Array<{
  provider: SignupProvider;
  label: string;
  icon: string;
  enabled: boolean;
}> = [
  { provider: 'google', label: 'Sign up with Google', icon: googleLogo, enabled: true },
  { provider: 'classlink', label: 'Sign up with ClassLink', icon: classLinkLogo, enabled: true },
  { provider: 'clever', label: 'Sign up with Clever', icon: cleverLogo, enabled: true },
];

export function SignupAccountMethods({
  isProductView,
  requiredProvider,
  canAttemptSignup,
  isWorking,
  emailAddress,
  hasAcceptedTerms,
  showConsentError,
  showEmailError = false,
  actionError,
  onEmailChange,
  onTermsChange,
  onProviderSignUp,
}: SignupAccountMethodsProps) {
  const theme = useTheme();

  // Email submits the containing signup form so validation and consent stay with its owner.
  return (
    <Box component="section" aria-labelledby="account-heading" sx={signupAccountMethodsStyles.productSection()}>
      <Typography id="account-heading" component="h2" sx={signupAccountMethodsStyles.accountHeading()}>{isProductView ? 'Your account' : 'Create your account'}</Typography>
      {!isProductView && <Typography sx={signupAccountMethodsStyles.sectionDescription(theme)}>Choose how you’d like to sign up.</Typography>}
      {actionError && <Typography id="signup-action-error" role="alert" sx={signupAccountMethodsStyles.actionError(theme)}>{actionError}</Typography>}
      <Box sx={signupAccountMethodsStyles.providerStack()}>
        {providerOptions.filter((option) => !isProductView || option.provider === 'google').map((option) => {
          const isProviderEnabled = option.enabled && (!requiredProvider || option.provider === requiredProvider);
          return (
            <Button key={option.provider} type="button" variant="contained" disableElevation disabled={!isProviderEnabled || !canAttemptSignup} aria-describedby={isProviderEnabled && showConsentError ? 'signup-action-error' : undefined} onClick={() => void onProviderSignUp(option.provider)} sx={signupAccountMethodsStyles.providerButton(theme)}>
              <Box sx={signupAccountMethodsStyles.providerIconFrame()}><Box component="img" src={option.icon} alt="" aria-hidden="true" sx={signupAccountMethodsStyles.providerIcon()} /></Box>
              <Box component="span" sx={signupAccountMethodsStyles.providerLabel()}>{option.label}</Box>
            </Button>
          );
        })}
      </Box>
      <Box sx={signupAccountMethodsStyles.emailDivider(theme)}>or use your email</Box>
      <Box sx={signupAccountMethodsStyles.emailSignupRow()}>
        <TextField id="signup-email-address" type="email" required fullWidth error={showEmailError} placeholder="Use your official school or district email" value={emailAddress} disabled={isWorking || Boolean(requiredProvider)} onChange={(event) => onEmailChange(event.target.value)} slotProps={{htmlInput: {'aria-label': 'Email address', 'aria-describedby': showEmailError ? 'signup-action-error' : undefined}}} sx={signupAccountMethodsStyles.input()} />
        <Button type="submit" formNoValidate fullWidth variant="contained" disableElevation disabled={!canAttemptSignup || Boolean(requiredProvider)} aria-describedby={!requiredProvider && (showConsentError || showEmailError) ? 'signup-action-error' : undefined} sx={signupAccountMethodsStyles.emailButton(theme)}>{isWorking ? 'Starting signup…' : 'Sign up with email'}</Button>
      </Box>
      <Box sx={signupAccountMethodsStyles.consentArea()}>
        <FormControlLabel sx={signupAccountMethodsStyles.consentLabel()} control={<Checkbox checked={hasAcceptedTerms} onChange={(event) => onTermsChange(event.target.checked)} inputProps={{'aria-invalid': showConsentError, 'aria-describedby': showConsentError ? 'signup-action-error' : undefined}} />} label={<span>I’ve read and accept the <Link href="https://www.exploros.com/privacy/" target="_blank" rel="noopener noreferrer" sx={signupAccountMethodsStyles.accentLink(theme)}>privacy policy</Link> and <Link href="/terms" target="_blank" rel="noopener noreferrer" sx={signupAccountMethodsStyles.accentLink(theme)}>terms and conditions</Link>.</span>} />
      </Box>
    </Box>
  );
}
